import { useResultAmendmentHistoryQuery } from '@/types/vue-apollo-helpers';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { can, AppPermissions } from '@/utils/permissions';
import type { Ref } from 'vue';
import type { ResultAmendmentEntry } from '@/components/DashBoard/Results/ViewResults/ResultAmendmentTimeline.vue';

/**
 * Post-publish score corrections for one result (#227).
 *
 * The server gates `resultAmendmentHistory` on `update_student_score` — whose
 * frontend mirror is `results.upload_scores`, the same permission that unlocks
 * the inline score grid (`useScoreEntry.hasScorePermission`). Checking it here
 * keeps a user without it from firing a query that can only come back denied.
 */
export function useResultAmendmentHistory(
  studentId: Ref<string | null | undefined>,
  academicCalendarId: Ref<string | null | undefined>,
) {
  const userStore = useUserStore();
  const { currentUser } = storeToRefs(userStore);

  const canViewAmendments = computed(() =>
    can(currentUser.value, AppPermissions.Results.Actions.UploadScores, AppPermissions.Results.Resource));

  const enabled = computed(
    () => canViewAmendments.value && !!studentId.value && !!academicCalendarId.value,
  );

  const { result, loading, refetch } = useResultAmendmentHistoryQuery(
    () => ({
      student_id: String(studentId.value ?? ''),
      academic_calendar_id: String(academicCalendarId.value ?? ''),
    }),
    () => ({ enabled: enabled.value, fetchPolicy: 'cache-and-network' }),
  );

  const amendments = computed<ResultAmendmentEntry[]>(
    () => result.value?.resultAmendmentHistory ?? [],
  );

  return {
    canViewAmendments,
    amendments,
    loadingAmendments: loading,
    refetchAmendments: refetch,
  };
}
